import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import api from "../services/api";
import { useSEO, SEO_PAGES } from "../hooks/useSEO";

/* ── Palette ── */
const C = {
  saffron:"#E8922A", saffronL:"#FDF0D5", saffronD:"#B86A10",
  sage:"#4A7C59",    sageL:"#EAF4EE",    sageD:"#2D5A3D",
  terra:"#B85C38",
  cream:"#FDFAF3",   creamD:"#F5EDD8",
  turmeric:"#F7C948",
  ink:"#2C1810",     inkMid:"#5C3D2E",   inkLight:"#9A7B60",
};

const BACKEND =
  window.location.hostname === "localhost"
    ? "http://localhost:5000"
    : "https://clinic-opd-backend.onrender.com";

const resolvePhoto = (photo) => {
  if (!photo) return null;
  return photo.startsWith("http") ? photo : `${BACKEND}${photo}`;
};

/* ── SVGs ── */
const Lotus = ({ size=48, color=C.saffron }) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none">
    {[0,30,60,-30,-60,90].map((r,i) => (
      <ellipse key={i} cx="40" cy="50" rx="7" ry="16"
        fill={color} opacity={i>3?0.45:i>1?0.7:0.95}
        transform={`rotate(${r} 40 50)`}/>
    ))}
    <circle cx="40" cy="44" r="7" fill={C.turmeric}/>
  </svg>
);

function SectionTag({ label, light }) {
  return (
    <div style={{
      display:"inline-flex", alignItems:"center", gap:6, marginBottom:12,
      background: light ? "rgba(255,255,255,0.15)" : C.saffronL,
      border: light ? "1px solid rgba(255,255,255,0.25)" : `1px solid ${C.saffron}40`,
      borderRadius:20, padding:"5px 18px",
    }}>
      <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.13em",
        color: light ? "rgba(255,255,255,0.9)" : C.saffronD,
        textTransform:"uppercase", fontFamily:"'Inter',sans-serif" }}>{label}</span>
    </div>
  );
}

/* ══════════════════════════════════════
   DOCTOR CARD  — new design
══════════════════════════════════════ */
function DoctorCard({ doc, index }) {
  const [flipped, setFlipped] = useState(false);
  const photoUrl = resolvePhoto(doc.photo);

  const expertiseList = (doc.expertise || doc.specialization || "")
    .split(",").map(s => s.trim()).filter(Boolean).slice(0, 3);

  return (
    <motion.div
      initial={{ opacity:0, y:40 }}
      whileInView={{ opacity:1, y:0 }}
      viewport={{ once:true }}
      transition={{ delay: index * 0.08, duration: 0.5 }}
      onClick={() => setFlipped(f => !f)}
      style={{
        cursor:"pointer",
        perspective:1200,
        position:"relative",
        /* fixed height so card doesn't collapse */
        height: 520,
      }}
    >
      <motion.div
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={{ duration: 0.65, ease:[0.4,0,0.2,1] }}
        style={{
          width:"100%", height:"100%",
          transformStyle:"preserve-3d",
          position:"relative",
        }}
      >

        {/* ══ FRONT ══ */}
        <div style={{
          position:"absolute", inset:0,
          backfaceVisibility:"hidden", WebkitBackfaceVisibility:"hidden",
          borderRadius:24,
          overflow:"hidden",
          boxShadow:"0 16px 56px rgba(44,24,16,0.14)",
          background:"#fff",
          display:"flex", flexDirection:"column",
        }}>

          {/* ── Photo — 60% of card height ── */}
          <div style={{
            height:"60%",
            flexShrink:0,
            position:"relative",
            overflow:"hidden",
            background:`linear-gradient(160deg,${C.sageD} 0%,${C.sage} 60%,${C.saffron} 100%)`,
          }}>
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={doc.name}
                style={{
                  width:"100%", height:"100%",
                  objectFit:"cover",
                  /* center horizontally, show face from top */
                  objectPosition:"center 15%",
                  display:"block",
                }}
              />
            ) : (
              <div style={{ width:"100%", height:"100%", display:"flex",
                alignItems:"center", justifyContent:"center" }}>
                <span style={{ fontSize:80, opacity:0.5 }}>🩺</span>
              </div>
            )}

            {/* Dark gradient at bottom so name is readable */}
            <div style={{
              position:"absolute", inset:0,
              background:"linear-gradient(to bottom, transparent 40%, rgba(20,10,4,0.72) 100%)",
              pointerEvents:"none",
            }}/>

            {/* Name + qualification overlay */}
            <div style={{
              position:"absolute", bottom:0, left:0, right:0,
              padding:"20px 20px 16px",
            }}>
              <h3 style={{
                fontFamily:"'Playfair Display',serif",
                fontSize:22, fontWeight:800,
                color:"#fff", margin:0,
                textShadow:"0 2px 10px rgba(0,0,0,0.4)",
              }}>Dr. {doc.name}</h3>
              <p style={{
                fontSize:12, color:"rgba(255,255,255,0.85)",
                margin:"4px 0 0", fontFamily:"'Inter',sans-serif",
                textShadow:"0 1px 6px rgba(0,0,0,0.3)",
              }}>
                {[doc.qualification, doc.experience ? `${doc.experience}+ yrs` : ""].filter(Boolean).join(" · ")}
              </p>
            </div>

            {/* Flip badge */}
            <div style={{
              position:"absolute", top:12, right:12,
              background:"rgba(0,0,0,0.35)", backdropFilter:"blur(8px)",
              borderRadius:20, padding:"4px 12px",
              fontSize:10, fontWeight:700, color:"#fff",
              fontFamily:"'Inter',sans-serif",
              border:"1px solid rgba(255,255,255,0.25)",
            }}>
              Tap to flip ↩
            </div>
          </div>

          {/* ── Info body — 40% ── */}
          <div style={{
            flex:1,
            padding:"16px 20px 18px",
            display:"flex", flexDirection:"column", gap:10,
            overflow:"hidden",
          }}>

            {/* Expertise tags */}
            {expertiseList.length > 0 && (
              <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                {expertiseList.map((ex, i) => (
                  <span key={i} style={{
                    fontSize:10, fontWeight:700, padding:"4px 10px", borderRadius:20,
                    background: i % 2 === 0 ? C.saffronL : C.sageL,
                    color: i % 2 === 0 ? C.saffronD : C.sageD,
                    fontFamily:"'Inter',sans-serif",
                    textTransform:"uppercase", letterSpacing:"0.05em",
                  }}>{ex}</span>
                ))}
              </div>
            )}

            {/* Bio — clamped to 2 lines */}
            {doc.bio && (
              <p style={{
                fontSize:13, color:C.inkMid, lineHeight:1.65,
                fontFamily:"'Merriweather',serif",
                margin:0,
                display:"-webkit-box", WebkitLineClamp:2,
                WebkitBoxOrient:"vertical", overflow:"hidden",
                flex:1,
              }}>{doc.bio}</p>
            )}

            {/* Timing + Fee row */}
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              {doc.timing && (
                <div style={{
                  display:"flex", alignItems:"center", gap:5,
                  background:C.sageL, borderRadius:10, padding:"6px 11px",
                  flex:1, minWidth:0,
                }}>
                  <span style={{ fontSize:13 }}>🕐</span>
                  <span style={{ fontSize:11, color:C.sageD, fontWeight:700,
                    fontFamily:"'Inter',sans-serif",
                    overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                  }}>{doc.timing}</span>
                </div>
              )}
              {doc.consultFee && (
                <div style={{
                  display:"flex", alignItems:"center", gap:5,
                  background:C.saffronL, borderRadius:10, padding:"6px 11px",
                }}>
                  <span style={{ fontSize:13 }}>💰</span>
                  <span style={{ fontSize:11, color:C.saffronD, fontWeight:700,
                    fontFamily:"'Inter',sans-serif" }}>{doc.consultFee}</span>
                </div>
              )}
            </div>

            {/* Book button */}
            <motion.a
              href="/request"
              whileHover={{ scale:1.03, boxShadow:"0 10px 28px rgba(232,146,42,0.45)" }}
              whileTap={{ scale:0.97 }}
              onClick={e => e.stopPropagation()}
              style={{
                display:"flex", alignItems:"center", justifyContent:"center", gap:7,
                padding:"11px 0", borderRadius:14, marginTop:2,
                background:`linear-gradient(135deg,${C.saffron},${C.terra})`,
                color:"#fff", textDecoration:"none",
                fontSize:13, fontWeight:700,
                fontFamily:"'Inter',sans-serif",
                boxShadow:`0 5px 18px rgba(232,146,42,0.28)`,
              }}>
              🌿 Book Appointment
            </motion.a>
          </div>
        </div>

        {/* ══ BACK (Insight) ══ */}
        <div style={{
          position:"absolute", inset:0,
          backfaceVisibility:"hidden", WebkitBackfaceVisibility:"hidden",
          transform:"rotateY(180deg)",
          borderRadius:24, overflow:"hidden",
          boxShadow:"0 16px 56px rgba(44,24,16,0.18)",
          background:`linear-gradient(155deg,${C.sageD} 0%,${C.sage} 55%,${C.saffron} 130%)`,
          display:"flex", flexDirection:"column",
          padding:"28px 26px", gap:16,
        }}>
          {/* Decorative lotus bg */}
          <div style={{ position:"absolute", right:-20, top:-20, opacity:0.07, pointerEvents:"none" }}>
            <Lotus size={180} color="#fff"/>
          </div>
          <div style={{ position:"absolute", left:-15, bottom:-15, opacity:0.05, pointerEvents:"none" }}>
            <Lotus size={130} color={C.turmeric}/>
          </div>

          {/* Doctor mini header */}
          <div style={{ display:"flex", alignItems:"center", gap:12, zIndex:1 }}>
            <div style={{
              width:54, height:54, borderRadius:"50%", overflow:"hidden", flexShrink:0,
              border:"2.5px solid rgba(255,255,255,0.45)",
              background:`linear-gradient(135deg,${C.turmeric},${C.saffron})`,
              display:"flex", alignItems:"center", justifyContent:"center",
            }}>
              {photoUrl
                ? <img src={photoUrl} alt={doc.name}
                    style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"center 15%" }}/>
                : <span style={{ fontSize:22 }}>🩺</span>}
            </div>
            <div>
              <p style={{ fontSize:14, fontWeight:800, color:"#fff",
                fontFamily:"'Inter',sans-serif", margin:0 }}>Dr. {doc.name}</p>
              <p style={{ fontSize:11, color:"rgba(255,255,255,0.68)",
                fontFamily:"'Inter',sans-serif", margin:"2px 0 0" }}>
                {doc.qualification || "Ayurvedic Physician"}
              </p>
            </div>
          </div>

          {/* Divider */}
          <div style={{ height:1, background:"rgba(255,255,255,0.15)", zIndex:1 }}/>

          {/* Insight */}
          <div style={{ zIndex:1, flex:1, display:"flex", flexDirection:"column", gap:10 }}>
            <div style={{
              display:"inline-flex", alignItems:"center", gap:6,
              background:"rgba(255,255,255,0.15)", borderRadius:20,
              padding:"5px 14px", border:"1px solid rgba(255,255,255,0.25)",
              alignSelf:"flex-start",
            }}>
              <span style={{ fontSize:13 }}>💡</span>
              <span style={{ fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.92)",
                fontFamily:"'Inter',sans-serif", letterSpacing:"0.05em" }}>
                {doc.insightsTitle || "Health Insight"}
              </span>
            </div>
            <p style={{
              fontFamily:"'Merriweather',serif",
              fontSize:14, color:"rgba(255,255,255,0.93)",
              lineHeight:1.85, margin:0,
            }}>
              {doc.insights ||
                "Good health begins with a balanced daily routine, proper nutrition, and mindful living — the cornerstones of Ayurvedic wellness."}
            </p>
          </div>

          {/* Flip back */}
          <motion.div
            whileTap={{ scale:0.97 }}
            style={{
              zIndex:1, cursor:"pointer",
              display:"flex", alignItems:"center", justifyContent:"center", gap:6,
              background:"rgba(255,255,255,0.13)", borderRadius:12,
              padding:"10px 16px", border:"1px solid rgba(255,255,255,0.22)",
            }}>
            <span style={{ fontSize:12, color:"rgba(255,255,255,0.85)",
              fontFamily:"'Inter',sans-serif", fontWeight:700 }}>
              ↩ Tap to flip back
            </span>
          </motion.div>
        </div>

      </motion.div>
    </motion.div>
  );
}

/* ══════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════ */
export default function OurDoctors() {
  useSEO(SEO_PAGES.ourDoctors);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cms,     setCms]     = useState({});

  useEffect(() => {
    api.get("/doctor-profiles")
      .then(res => setDoctors(res.data?.data || res.data || []))
      .catch(() => setDoctors([]))
      .finally(() => setLoading(false));
    api.get("/content/our-doctors")
      .then(res => setCms(res.data?.data || {}))
      .catch(() => {});
  }, []);

  return (
    <div style={{ background:C.cream, minHeight:"100vh", paddingTop:72 }}>

      {/* ── HERO ── */}
      <section style={{
        padding:"88px 24px 80px",
        background:`linear-gradient(145deg,${C.cream} 0%,${C.creamD} 50%,${C.saffronL} 100%)`,
        textAlign:"center", position:"relative", overflow:"hidden",
      }}>
        <div style={{ position:"absolute", right:-60, top:-60, opacity:0.05, pointerEvents:"none" }}>
          <Lotus size={340} color={C.saffron}/>
        </div>
        <div style={{ position:"absolute", left:-40, bottom:-30, opacity:0.04, pointerEvents:"none" }}>
          <Lotus size={220} color={C.sage}/>
        </div>

        <motion.div initial={{ opacity:0, y:32 }} animate={{ opacity:1, y:0 }}
          transition={{ duration:0.65 }}
          style={{ position:"relative", zIndex:2, maxWidth:740, margin:"0 auto" }}>
          <SectionTag label={cms.odSectionTag || "Meet Our Physicians"}/>
          <h1 style={{
            fontFamily:"'Playfair Display',serif",
            fontSize:"clamp(34px,5.5vw,60px)", fontWeight:800,
            color:C.ink, lineHeight:1.15, marginBottom:16,
          }}>{cms.odTitle || "Our Doctors"}</h1>

          <motion.div initial={{ scaleX:0 }} animate={{ scaleX:1 }}
            transition={{ delay:0.4, duration:0.5 }}
            style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:14, marginBottom:22 }}>
            <div style={{ height:1, width:60, background:`linear-gradient(90deg,transparent,${C.saffron})` }}/>
            <Lotus size={36} color={C.saffron}/>
            <div style={{ height:1, width:60, background:`linear-gradient(90deg,${C.saffron},transparent)` }}/>
          </motion.div>

          <p style={{ fontSize:"clamp(15px,2vw,18px)", color:C.inkMid, lineHeight:1.85,
            fontFamily:"'Merriweather',serif" }}>
            {cms.odSubtitle || "Experienced Ayurvedic physicians — each with deep expertise, classical training, and a passion for holistic healing."}
          </p>

          <motion.div
            initial={{ opacity:0 }} animate={{ opacity:1 }}
            transition={{ delay:0.7 }}
            style={{
              marginTop:22, display:"inline-flex", alignItems:"center", gap:7,
              background:"rgba(74,124,89,0.09)", borderRadius:20,
              padding:"8px 18px", border:`1px solid ${C.sage}25`,
            }}>
            <span style={{ fontSize:14 }}>👆</span>
            <span style={{ fontSize:13, color:C.inkMid, fontFamily:"'Inter',sans-serif", fontWeight:600 }}>
              {cms.odFlipHint || "Tap any card to flip and read the doctor's health insight"}
            </span>
          </motion.div>
        </motion.div>
      </section>

      {/* ── CARDS ── */}
      <section style={{ padding:"80px 24px" }}>
        <div style={{ maxWidth:1140, margin:"0 auto" }}>
          {loading ? (
            <div style={{ textAlign:"center", padding:"80px 0" }}>
              <motion.div animate={{ rotate:360 }}
                transition={{ duration:1.2, repeat:Infinity, ease:"linear" }}>
                <Lotus size={52} color={C.saffron}/>
              </motion.div>
              <p style={{ marginTop:18, color:C.inkLight, fontFamily:"'Inter',sans-serif", fontSize:15 }}>
                Loading doctors…
              </p>
            </div>
          ) : doctors.length === 0 ? (
            <div style={{ textAlign:"center", padding:"80px 24px" }}>
              <Lotus size={56} color={C.saffron}/>
              <p style={{ marginTop:18, color:C.inkLight, fontFamily:"'Merriweather',serif", fontSize:16 }}>
                {cms.odEmptyText || "Doctor profiles coming soon."}
              </p>
            </div>
          ) : (
            <div style={{
              display:"grid",
              gridTemplateColumns:"repeat(auto-fill, minmax(min(300px, 100%), 1fr))",
              gap:32,
            }}>
              {doctors.map((doc, i) => (
                <DoctorCard key={doc._id || i} doc={doc} index={i}/>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{ padding:"72px 24px", background:"#fff", textAlign:"center" }}>
        <motion.div initial={{ opacity:0, y:20 }} whileInView={{ opacity:1, y:0 }}
          viewport={{ once:true }}>
          <motion.div animate={{ y:[-5,5,-5] }} transition={{ duration:3, repeat:Infinity }}>
            <Lotus size={54} color={C.saffron}/>
          </motion.div>
          <h2 style={{ fontFamily:"'Playfair Display',serif",
            fontSize:"clamp(22px,3.5vw,38px)", fontWeight:700,
            color:C.ink, margin:"16px 0 10px" }}>{cms.odCtaHeading || "Ready to Begin Your Healing?"}</h2>
          <p style={{ fontSize:16, color:C.inkLight, marginBottom:30,
            fontFamily:"'Merriweather',serif" }}>
            {cms.odCtaSub || "Book a consultation with our expert physicians today."}
          </p>
          <motion.a href="/request"
            whileHover={{ scale:1.04, boxShadow:"0 16px 36px rgba(232,146,42,0.5)" }}
            whileTap={{ scale:0.97 }}
            style={{
              display:"inline-flex", alignItems:"center", gap:9,
              padding:"14px 36px", borderRadius:50, fontSize:15, fontWeight:700,
              textDecoration:"none", fontFamily:"'Inter',sans-serif",
              background:`linear-gradient(135deg,${C.saffron},${C.terra})`,
              color:"#fff", boxShadow:`0 8px 26px rgba(232,146,42,0.38)`,
            }}>
            🌿 {cms.odCtaBtn || "Book Appointment"}
          </motion.a>
        </motion.div>
      </section>

      {/* Mobile responsiveness */}
      <style>{`
        @media (max-width: 480px) {
          /* slightly shorter card on small phones */
        }
      `}</style>
    </div>
  );
}